/* @moldura padrao */
/**
 * page.tsx (despesas) — Tela INTERNA do financeiro. Tema AdminUX (Bootstrap).
 *
 * Lista de despesas com filtros + total. Aqui só lista, navega e remove — criar
 * e editar são páginas dedicadas.
 *
 * Esta tela é da GESTÃO (o backend recusa com 403 os demais), e o botão dela vai
 * para `/despesas/lancar`: o formulário COMPLETO, de escritório. É o caminho da
 * Patrícia lançando a notinha que o instalador entregou, ou abrindo uma despesa
 * para ajustar. Quem lança em campo entra por outra porta — o atalho do rodapé,
 * que vai direto a `/despesas/nova` e nunca passa por esta lista.
 *
 * ─── O REDESENHO DE 24/09 (ficha #40) ───────────────────────────────────────
 * Molde: a tabela de Turnos do rm-ops — filtros num cartão próprio, tabela em
 * outro, fonte menor, linha de apoio em cinza embaixo da principal. O que mudou,
 * e por quê:
 *   - O PERÍODO é o `CustomCampoIntervalo` pequeno, no cartão de filtros. Os
 *     dois `<input type="date">` de antes eram controle nativo — o popup do
 *     navegador, com o ícone do Dashlane dentro.
 *   - TODA LINHA TEM UMA ALTURA: histórico e categoria lado a lado, cada um numa
 *     linha só (`text-nowrap` + truncar). Empilhar os dois deixava linhas de
 *     alturas diferentes, e ficou feio.
 *   - LOJA virou sigla (`BZ`, `TR`), num selo pequeno.
 *   - OS virou ícone: o que se quer saber batendo o olho é SE tem OS. O número
 *     fica no `title`, e o clique abre a OS.
 *   - ORIGEM saiu (coluna e filtro). "Campo" ou "Financeiro" diz por qual
 *     formulário a despesa entrou, e nem quem usa a tela sabia o que era.
 *   - A RESTITUIÇÃO deixou de ter botão com texto na linha: é um ícone ao lado
 *     da forma de pagamento, e o filtro "A restituir" acha as pendentes.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomPaginacao, usePaginacao } from '@/components/CustomPaginacao';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';
import { formatarData } from '@/lib/data';
import type { Categoria, DespesaSaida, FormaPgtoOpcao, Loja } from './shared';
import { formaCurta, formatarValor, rotuloFormaPgto } from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomCampoIntervalo } from '@/components/CustomCamposData';

const TODOS = 'todos';
/** Valor do select que pede o que NÃO tem (sem categoria, sem viagem) — 26/09. */
const NENHUMA = 'nenhuma';

/**
 * A despesa vista pelo Tiny, num ícone (dele, 25/09: "algum ícone que
 * represente se a despesa foi lançada ou não"). Vazio = nunca foi.
 */
function iconeTiny(d: DespesaSaida): { icone: string; cor: string; titulo: string } | null {
  const conta = d.conta_pagar_tiny ? ` · conta ${d.conta_pagar_tiny}` : '';
  if (d.envio_tiny === 'verificar' || d.envio_tiny === 'enviando')
    return { icone: 'bi-lock-fill', cor: 'text-danger', titulo: 'Com cadeado: o Tiny não confirmou' };
  if (d.envio_tiny === 'enviada' && (d.situacao_tiny === 'pago' || d.situacao_tiny === 'parcial'))
    return { icone: 'bi-cash-coin', cor: 'text-success', titulo: `Paga no Tiny${conta}` };
  if (d.envio_tiny === 'enviada')
    return { icone: 'bi-check-circle-fill', cor: 'text-success', titulo: `No Tiny${conta}` };
  if (d.envio_tiny === 'ja_no_tiny')
    return { icone: 'bi-check2-all', cor: 'text-success', titulo: `Lançada à mão no Tiny${conta || ' · sem vínculo ainda'}` };
  if (d.situacao_tiny === 'excluida' || d.situacao_tiny === 'cancelada')
    return { icone: 'bi-x-circle', cor: 'text-secondary', titulo: `${d.situacao_tiny === 'excluida' ? 'Excluída' : 'Cancelada'} no Tiny — pode ser enviada de novo` };
  return null;
}

export function DespesasPage() {
  const navigate = useNavigate();
  // `/despesas/lista/bz`: a página de UMA loja (25/09). A loja fica presa no
  // filtro, some o alternador, e "Nova despesa" já lança nela.
  const { loja: lojaDaRota } = useParams();
  const [lista, setLista] = useState<DespesaSaida[]>([]);
  // A escolha de "quantos por vez" vem guardada do navegador.
  const pg = usePaginacao('despesas');
  // A soma dos valores FILTRADOS, vinda do backend em Decimal.
  const [soma, setSoma] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // Opções para filtros
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [formas, setFormas] = useState<FormaPgtoOpcao[]>([]);

  // Filtros
  const [fLoja, setFLoja] = useState(TODOS);
  const [fCategoria, setFCategoria] = useState(TODOS);
  const [fViagem, setFViagem] = useState(TODOS);
  // A busca digitada e a que vale: espera o dedo parar (400 ms) antes de ir
  // ao servidor — senão "jantar" são seis buscas.
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [fBusca, setFBusca] = useState('');
  const [viagens, setViagens] = useState<{ id: number; label: string; encerrada: boolean }[]>([]);
  const [fForma, setFForma] = useState(TODOS);
  const [fRestPendente, setFRestPendente] = useState(false);
  const [fDe, setFDe] = useState('');
  const [fAte, setFAte] = useState('');

  // Confirmação de remoção
  // Duas vistas (25/09): a lista de TRABALHO e as ARQUIVADAS. Arquivar tira
  // da frente sem apagar; apagar mudou para dentro da despesa.
  const [vista, setVista] = useState<'trabalho' | 'arquivadas'>('trabalho');
  const { pode } = useAuth();
  const [conferindo, setConferindo] = useState(false);

  /** Pergunta ao Tiny por todas as enviadas: o que foi excluído lá é liberado aqui. */
  async function conferirComTiny() {
    setConferindo(true);
    try {
      const r = await api.post<{ conferidas: number; liberadas: number; falhas: number }>(
        `/despesas/tiny/conferir${fLoja !== TODOS ? `?loja_id=${fLoja}` : ''}`,
      );
      toast.success(
        `${r.conferidas} conferida(s) no Tiny` +
          (r.liberadas ? ` · ${r.liberadas} tinha(m) sido excluída(s) lá e foram liberadas` : '') +
          (r.falhas ? ` · ${r.falhas} sem resposta` : ''),
      );
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível conferir com o Tiny.');
    } finally {
      setConferindo(false);
    }
  }

  useEffect(() => {
    api.get<Loja[]>('/lojas').then(setLojas).catch(() => undefined);
    api
      .get<Categoria[]>('/despesas/categorias')
      .then(setCategorias)
      .catch(() => undefined);
    // A lista COMPLETA de formas: esta é tela de escritório. A lista fixa do
    // formulário público, que o filtro usava, não tinha os dois cartões.
    api
      .get<FormaPgtoOpcao[]>('/despesas/formas-pagamento?todos=true')
      .then(setFormas)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setFBusca(buscaDigitada.trim()), 400);
    return () => clearTimeout(t);
  }, [buscaDigitada]);

  useEffect(() => {
    api
      .get<{ id: number; label: string; encerrada: boolean }[]>('/despesas/viagens')
      .then(setViagens)
      .catch(() => undefined);
  }, []);

  const lojaFixa = lojaDaRota
    ? lojas.find((l) => (l.codigo ?? '').toLowerCase() === lojaDaRota.toLowerCase()) ?? null
    : null;

  // Trocar de página (Búzios → Trancoso → Todas) é o mesmo componente: o
  // filtro de loja acompanha a rota.
  useEffect(() => {
    setFLoja(lojaFixa ? String(lojaFixa.id) : TODOS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lojaFixa?.id, lojaDaRota]);

  /** id da loja → sigla ("BZ"). A API da despesa manda só o nome. */
  const siglaDaLoja = useMemo(() => {
    const m = new Map<number, string>();
    for (const l of lojas) m.set(l.id, l.codigo ?? l.nome_curto ?? l.nome);
    return m;
  }, [lojas]);

  async function carregar() {
    // Página de loja: espera a loja ser reconhecida — sem isto a lista pisca
    // com TODAS antes de filtrar.
    if (lojaDaRota && (!lojaFixa || fLoja !== String(lojaFixa.id))) return;
    setCarregando(true);
    setErro(null);
    try {
      const params = new URLSearchParams();
      if (fLoja !== TODOS) params.set('loja_id', fLoja);
      if (fCategoria === NENHUMA) params.set('sem_categoria', 'true');
      else if (fCategoria !== TODOS) params.set('categoria_id', fCategoria);
      if (fViagem === NENHUMA) params.set('sem_viagem', 'true');
      else if (fViagem !== TODOS) params.set('viagem_id', fViagem);
      if (fBusca) params.set('busca', fBusca);
      if (vista === 'arquivadas') params.set('arquivada', 'true');
      if (fForma !== TODOS) params.set('forma_pgto', fForma);
      if (fRestPendente) params.set('restituicao', 'pendente');
      if (fDe) params.set('de', fDe);
      if (fAte) params.set('ate', fAte);
      params.set('pagina', String(pg.pagina));
      params.set('limite', String(pg.porPagina));
      const qs = params.toString();
      const { itens, total, cabecalhos } = await api.getPaginado<DespesaSaida[]>(
        `/despesas${qs ? `?${qs}` : ''}`,
      );
      setLista(itens);
      pg.setTotal(total);
      setSoma(Number(cabecalhos.get('X-Soma-Valor') ?? 0));
    } catch (err) {
      setErro(
        err instanceof ApiError
          ? err.message
          : 'Não foi possível carregar as despesas.',
      );
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, fLoja, fCategoria, fViagem, fBusca, fForma, fRestPendente, fDe, fAte, pg.pagina, pg.porPagina]);

  // Mudar filtro volta para a página 1: o resultado é outro, e continuar na
  // página 7 mostraria lista vazia sem explicar por quê.
  useEffect(() => {
    pg.setPagina(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, fLoja, fCategoria, fViagem, fBusca, fForma, fRestPendente, fDe, fAte]);

  async function marcarRestituido(id: number, restituido: boolean) {
    try {
      await api.post(`/despesas/${id}/restituicao?restituido=${restituido}`);
      toast.success(restituido ? 'Marcada como restituída.' : 'Restituição desfeita.');
      carregar();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível atualizar.',
      );
    }
  }

  // O GATILHO DISPAROU — 24/08. Este total era somado aqui em JS, com a exceção
  // escrita ao lado dizendo "morre quando entrar paginação". A paginação entrou
  // no mesmo dia. Agora a soma vem do backend (`X-Soma-Valor`), calculada em
  // Decimal sobre TODAS as linhas do filtro, não sobre a página visível.
  const total = soma;

  const lojaPadrao = lojaFixa ? String(lojaFixa.id) : TODOS;
  const filtrado =
    fLoja !== lojaPadrao ||
    fViagem !== TODOS ||
    Boolean(fBusca) ||
    fCategoria !== TODOS ||
    fForma !== TODOS ||
    fRestPendente ||
    Boolean(fDe) ||
    Boolean(fAte);

  function limparFiltros() {
    // Numa página de loja, a loja não é filtro — é a página.
    setFLoja(lojaPadrao);
    setFViagem(TODOS);
    setBuscaDigitada('');
    setFBusca('');
    setFCategoria(TODOS);
    setFForma(TODOS);
    setFRestPendente(false);
    setFDe('');
    setFAte('');
  }

  /** Arquivar é reversível e não apaga nada — por isso sem modal, com
   *  "Desfazer" no próprio aviso. */
  async function arquivar(id: number, arquivar: boolean) {
    try {
      await api.post(`/despesas/${id}/${arquivar ? 'arquivar' : 'desarquivar'}`);
      toast.success(arquivar ? 'Despesa arquivada.' : 'Despesa de volta à lista.', {
        action: {
          label: 'Desfazer',
          onClick: () => {
            api
              .post(`/despesas/${id}/${arquivar ? 'desarquivar' : 'arquivar'}`)
              .then(() => carregar())
              .catch(() => undefined);
          },
        },
      });
      carregar();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível arquivar.');
    }
  }

  return (
    <CustomMolduraPagina
      titulo={lojaFixa ? `Despesas de ${lojaFixa.nome_curto ?? lojaFixa.nome}` : 'Despesas'}
      descricao={lojaFixa ? 'Só desta loja — e o que se lança aqui já é dela' : 'Todas as lojas'}
      caminho={lojaFixa ? [{ rotulo: 'Despesas', para: '/despesas' }] : []}
      rotuloNoCaminho={lojaFixa ? (lojaFixa.nome_curto ?? lojaFixa.nome) : undefined}
      acoes={
        <>
          {/* Ação da PÁGINA, não filtro: por isso mora aqui em cima. */}
          <button
            className="btn btn-outline-theme"
            onClick={conferirComTiny}
            disabled={conferindo}
            title="Pergunta ao Tiny por cada despesa enviada — a excluída lá volta a poder ser enviada"
          >
            {conferindo ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-arrow-repeat me-1"></i>
            )}
            Conferir com o Tiny
          </button>
          <button
            className="btn btn-theme"
            onClick={() =>
              navigate(lojaFixa ? `/despesas/lancar?loja=${lojaFixa.codigo}` : '/despesas/lancar')
            }
          >
            <i className="bi bi-plus-lg me-1"></i> Nova despesa
          </button>
        </>
      }
    >
      <ul className="nav nav-tabs adminuiux-tabs mb-3">
        {([
          ['trabalho', 'Em uso', 'bi-list-check'],
          ['arquivadas', 'Arquivadas', 'bi-archive'],
        ] as const).map(([k, rotulo, icone]) => (
          <li className="nav-item" key={k}>
            <button
              type="button"
              className={`nav-link ${vista === k ? 'active' : ''}`}
              onClick={() => setVista(k)}
            >
              <i className={`bi ${icone} me-1`}></i>
              {rotulo}
            </button>
          </li>
        ))}
      </ul>

      {/* Filtros — cartão próprio, como os Turnos do rm-ops. A loja é um
          alternador (são duas, e cabem à vista); o resto é select. */}
      <div className="card adminuiux-card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            {/* BUSCA (25/09): histórico, descrição, categoria e quem lançou. */}
            <div className="col-12 col-md">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-transparent">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Buscar no histórico, descrição, categoria ou quem lançou…"
                  value={buscaDigitada}
                  onChange={(e) => setBuscaDigitada(e.target.value)}
                  aria-label="Buscar despesa"
                />
              </div>
            </div>
            <div className="col-6 col-md-auto">
              <input
                type="checkbox"
                className="btn-check"
                id="despesas-a-restituir"
                checked={fRestPendente}
                onChange={(e) => setFRestPendente(e.target.checked)}
              />
              <label
                className="btn btn-sm btn-outline-theme w-100"
                htmlFor="despesas-a-restituir"
                title="Pagas com dinheiro ou cartão próprio e ainda não restituídas"
              >
                <i className="bi bi-arrow-left-right me-1"></i> A restituir
              </label>
            </div>
            {/* SEMPRE VISÍVEL, desabilitado quando não há filtro — aparecer e
                sumir fazia a barra dançar (lição dos Turnos do rm-ops). */}
            <div className="col-6 col-md-auto">
              <button
                className="btn btn-sm btn-outline-secondary w-100"
                onClick={limparFiltros}
                disabled={!filtrado}
                title={filtrado ? 'Tirar todos os filtros' : 'Nenhum filtro aplicado'}
              >
                <i className="bi bi-arrow-counterclockwise me-1"></i> Limpar
              </button>
            </div>
          </div>
          {/* Segunda linha: os filtros de ESCOLHA. */}
          <div className="row g-2 align-items-center mt-0">
            {/* O período mora AQUI, pequeno, e não na moldura: lá ele vinha com
                o tamanho de campo de formulário e ficou desproporcional ao lado
                do título (24/09). */}
            <div className="col-12 col-md-auto" style={{ width: 230 }}>
              <CustomCampoIntervalo
                inicio={fDe}
                fim={fAte}
                pequeno
                onChange={(i, f) => {
                  setFDe(i);
                  setFAte(f);
                }}
              />
            </div>
            {!lojaFixa && (
            <div className="col-12 col-md-auto">
              <div className="btn-group w-100" role="group" aria-label="Loja">
                {[{ id: TODOS, rotulo: 'Todas' }, ...lojas.map((l) => ({
                  id: String(l.id),
                  rotulo: l.codigo ?? l.nome_curto ?? l.nome,
                  titulo: l.nome_curto ?? l.nome,
                }))].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={`btn btn-sm ${fLoja === o.id ? 'btn-theme' : 'btn-outline-theme'}`}
                    onClick={() => setFLoja(o.id)}
                    title={'titulo' in o ? o.titulo : 'Todas as lojas'}
                  >
                    {o.rotulo}
                  </button>
                ))}
              </div>
            </div>
            )}
            {/* VIAGEM (25/09), com o mesmo rótulo da coluna: destino · período.
                As abertas primeiro — é nelas que ainda entra despesa. */}
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fViagem}
                onChange={(e) => setFViagem(e.target.value)}
                aria-label="Viagem"
              >
                <option value={TODOS}>Todas as viagens</option>
                <option value={NENHUMA}>Sem viagem</option>
                {[
                  ['Em aberto', viagens.filter((v) => !v.encerrada)],
                  ['Encerradas', viagens.filter((v) => v.encerrada)],
                ].map(([grupo, lista]) =>
                  (lista as typeof viagens).length ? (
                    <optgroup key={grupo as string} label={grupo as string}>
                      {(lista as typeof viagens).map((v) => (
                        <option key={v.id} value={String(v.id)}>
                          {v.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : null,
                )}
              </select>
            </div>
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fCategoria}
                onChange={(e) => setFCategoria(e.target.value)}
                aria-label="Categoria"
              >
                <option value={TODOS}>Todas as categorias</option>
                <option value={NENHUMA}>Sem categoria</option>
                {categorias.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-6 col-md-auto">
              <select
                className="form-select form-select-sm"
                value={fForma}
                onChange={(e) => setFForma(e.target.value)}
                aria-label="Forma de pagamento"
              >
                <option value={TODOS}>Todas as formas</option>
                {formas.map((f) => (
                  <option key={f.codigo} value={f.codigo}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card adminuiux-card">
        <div className="card-body">
          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle table-hover mb-0 small text-nowrap">
                <thead>
                  <tr className="text-secondary small text-uppercase">
                    <th className="text-nowrap">Data</th>
                    <th>Histórico</th>
                    <th>Categoria</th>
                    {!lojaFixa && <th>Loja</th>}
                    <th>Viagem</th>
                    <th>Pagamento</th>
                    <th>Quem lançou</th>
                    <th className="text-center" title="OS e comprovante">
                      <i className="bi bi-paperclip"></i>
                    </th>
                    <th className="text-center" title="Situação no Tiny">Tiny</th>
                    <th className="text-nowrap text-end">Valor</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((d) => {
                    // Toda linha tem UMA altura (24/09): histórico e categoria
                    // em colunas lado a lado, cada uma numa linha só. Sem
                    // histórico, cai para a descrição.
                    const historico = d.historico ?? d.descricao;
                    const forma = d.forma_pgto
                      ? formaCurta(
                          d.forma_pgto,
                          d.loja_cartao_id ? siglaDaLoja.get(d.loja_cartao_id) : null,
                        )
                      : null;
                    return (
                      <tr
                        key={d.id}
                        onClick={() => navigate(`/despesas/${d.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td className="text-nowrap">
                          {d.data ? formatarData(d.data) : '—'}
                        </td>
                        <td
                          className="text-truncate fw-medium"
                          style={{ maxWidth: 260 }}
                          title={d.descricao ?? d.historico ?? ''}
                        >
                          {/* PARCELA (migration 117): o selo "2/3" diz que ela
                              tem irmãs — o detalhe lista as outras. */}
                          {d.parcela_quantidade ? (
                            <span
                              className="badge text-bg-light border fw-normal me-1"
                              title={`Parcela ${d.parcela_numero} de ${d.parcela_quantidade}${d.vencimento ? ` · vence ${formatarData(d.vencimento)}` : ''}`}
                            >
                              {d.parcela_numero}/{d.parcela_quantidade}
                            </span>
                          ) : null}
                          {historico ?? <span className="text-secondary fw-normal">—</span>}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 170 }}>
                          {d.categoria_nome ? (
                            <span
                              className="badge bg-theme-1-subtle text-theme-1 fw-normal"
                              title={d.categoria_nome}
                            >
                              {d.categoria_nome}
                            </span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>
                        {!lojaFixa && (
                        <td>
                          {d.loja_id ? (
                            <span
                              className="badge text-bg-light border fw-normal"
                              title={d.loja_nome ?? ''}
                            >
                              {siglaDaLoja.get(d.loja_id) ?? d.loja_nome}
                            </span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>
                        )}
                        <td
                          className="text-secondary text-truncate"
                          style={{ maxWidth: 200 }}
                          title={d.viagem_titulo ?? d.viagem_label ?? ''}
                        >
                          {d.viagem_label ?? '—'}
                        </td>
                        <td className="text-nowrap">
                          {forma ? (
                            <span
                              className="badge text-bg-light border fw-normal"
                              title={rotuloFormaPgto(d.forma_pgto)}
                            >
                              <i className={`bi ${forma.icone} me-1`}></i>
                              {forma.rotulo}
                            </span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                          {d.restituicao === 'pendente' && (
                            <button
                              className="btn btn-link p-0 ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                marcarRestituido(d.id, true);
                              }}
                              title="A restituir — clique para marcar como restituída"
                              aria-label="Marcar como restituída"
                            >
                              <i className="bi bi-arrow-left-right text-warning-emphasis"></i>
                            </button>
                          )}
                          {d.restituicao === 'restituido' && (
                            <button
                              className="btn btn-link p-0 ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                marcarRestituido(d.id, false);
                              }}
                              title="Restituída — clique para desfazer"
                              aria-label="Desfazer restituição"
                            >
                              <i className="bi bi-check-circle-fill text-success"></i>
                            </button>
                          )}
                        </td>
                        <td className="text-secondary text-nowrap">
                          {d.lancado_por ?? '—'}
                        </td>
                        {/* OS e comprovante: o que importa é SE existem. */}
                        <td className="text-center text-nowrap">
                          {d.os_id ? (
                            <a
                              href={`/ordens-servico/${d.os_id}`}
                              className="me-2"
                              title={`OS ${d.os_codigo ?? ''}`}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/ordens-servico/${d.os_id}`);
                              }}
                            >
                              <i className="bi bi-clipboard-check"></i>
                            </a>
                          ) : (
                            <i
                              className="bi bi-clipboard me-2 text-secondary opacity-25"
                              title="Sem OS"
                            ></i>
                          )}
                          {d.comprovante ? (
                            <a
                              href={d.comprovante}
                              target="_blank"
                              rel="noreferrer"
                              title="Abrir comprovante"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="bi bi-receipt"></i>
                            </a>
                          ) : (
                            <i
                              className="bi bi-receipt text-secondary opacity-25"
                              title="Sem comprovante"
                            ></i>
                          )}
                        </td>
                        <td className="text-center">
                          {(() => {
                            const t = iconeTiny(d);
                            return t ? (
                              <i className={`bi ${t.icone} ${t.cor}`} title={t.titulo}></i>
                            ) : null;
                          })()}
                        </td>
                        <td className="text-nowrap text-end fw-semibold">
                          {formatarValor(d.valor)}
                        </td>
                        <td className="text-end">
                          {pode('despesas.lancamento.arquivar') && (
                            <button
                              className="btn btn-link p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                arquivar(d.id, vista === 'trabalho');
                              }}
                              title={vista === 'trabalho' ? 'Arquivar — sai da lista, não apaga' : 'Desarquivar — volta para a lista'}
                              aria-label={vista === 'trabalho' ? 'Arquivar despesa' : 'Desarquivar despesa'}
                            >
                              <i className={`bi ${vista === 'trabalho' ? 'bi-archive' : 'bi-box-arrow-up'} text-secondary`}></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {lista.length === 0 && (
                    <tr>
                      <td colSpan={lojaFixa ? 10 : 11} className="py-5 text-center text-secondary">
                        {vista === 'arquivadas' ? 'Nenhuma despesa arquivada.' : 'Nenhuma despesa encontrada.'}
                      </td>
                    </tr>
                  )}
                </tbody>
                {lista.length > 0 && (
                  <tfoot>
                    <tr className="fw-semibold border-top">
                      <td className="text-nowrap">Total</td>
                      <td colSpan={lojaFixa ? 7 : 8} className="text-secondary fw-normal">
                        {pg.total} lançamento{pg.total === 1 ? '' : 's'}
                      </td>
                      <td className="text-nowrap text-end">
                        {formatarValor(total)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {!carregando && !erro && (
            <CustomPaginacao
              tela="despesas"
              total={pg.total}
              pagina={pg.pagina}
              porPagina={pg.porPagina}
              onPagina={pg.setPagina}
              onPorPagina={pg.setPorPagina}
              rotulo="despesa"
            />
          )}
        </div>
      </div>

    </CustomMolduraPagina>
  );
}
